/**
 * Our Process Section – Custom Element
 *
 * Manages scroll-based visibility and media playback for the stacking
 * cards process section. All behavior is driven by IntersectionObserver;
 * no scroll event listeners are used.
 *
 * INTERSECTION OBSERVER LOGIC:
 * 1. Step Observer – watches each `.our-process__step` card.
 *    When a card enters the viewport (threshold 0.15), the class
 *    `our-process__step--visible` is added, triggering the CSS
 *    entry animation (fade-up). The class is added once and persists.
 *
 * 2. Media Observer – watches each `[data-step-media]` container.
 *    When a media container enters the viewport (threshold 0.3),
 *    any <video> inside is played. When it exits, the video pauses.
 *    Images use native `loading="lazy"` and need no JS control.
 *
 * SCROLL DIRECTION HANDLING:
 * The sticky + z-index stacking is handled entirely by CSS.
 * Scrolling DOWN: next card overlaps previous (higher z-index).
 * Scrolling UP:   top card slides back down (natural sticky un-stick).
 * No JS involvement is needed for the overlap transitions.
 */

if (!customElements.get('our-process-section')) {
  customElements.define(
    'our-process-section',
    class OurProcessSection extends HTMLElement {
      /** @type {IntersectionObserver|null} */
      stepObserver = null;

      /** @type {IntersectionObserver|null} */
      mediaObserver = null;

      constructor() {
        super();
      }

      connectedCallback() {
        this.steps = this.querySelectorAll('.our-process__step');
        this.mediaContainers = this.querySelectorAll('[data-step-media]');

        this.setupStepObserver();
        this.setupMediaObserver();
      }

      disconnectedCallback() {
        this.stepObserver?.disconnect();
        this.mediaObserver?.disconnect();
      }

      /**
       * Observes each step card for viewport entry.
       * Adds a visibility class that triggers the CSS entry animation.
       * Uses a low threshold (0.15) so the animation starts as the card
       * begins entering the viewport, creating a smooth reveal.
       */
      setupStepObserver() {
        this.stepObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('our-process__step--visible');
            }
          },
          {
            root: null,
            threshold: 0.15,
            rootMargin: '0px 0px -5% 0px'
          }
        );

        for (const step of this.steps) {
          this.stepObserver.observe(step);
        }
      }

      /**
       * Observes each media container for viewport presence.
       * Plays videos when visible, pauses when not. This ensures
       * off-screen videos don't consume resources.
       *
       * The threshold of 0.3 means the video starts playing once
       * 30% of the container is visible, giving a natural feel.
       */
      setupMediaObserver() {
        this.mediaObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const video = entry.target.querySelector('video');
              if (!video) continue;

              if (entry.isIntersecting) {
                video.play().catch(() => {
                  /* Autoplay may be blocked by browser policy – fail silently */
                });
              } else {
                video.pause();
              }
            }
          },
          {
            root: null,
            threshold: 0.3
          }
        );

        for (const media of this.mediaContainers) {
          this.mediaObserver.observe(media);
        }
      }
    }
  );
}
